import type { Address } from "viem"
import { useState } from "react"
import * as Form from "@radix-ui/react-form"
import { getAddress, isAddress } from "viem"

import { ShowWhenTrue } from "@/components/conditionals"
import { Search } from "@/components/search"
import { ShortMessageBox } from "@/domains/common/components/short-message-box"
import { WarningSquareIcon } from "@/domains/icons"
import { TokenStore } from "@/domains/tokens"

import { TokenList } from "../token-list"

/**
 * Local token manager component.
 * NOTE: This is a placeholder that will need to be adapted when local tokens
 * are migrated to use TokenStore instead of Token.
 * For now, it accepts TokenStore arrays and manages search state.
 */
export const TokenDialogLocalTokenManager: React.FC<{
  tokens: Array<TokenStore>
  onTokenSelect: (token: TokenStore) => void
  onAddToken?: (address: Address) => Promise<void>
  onRemoveToken?: (address: Address) => void
  onClose?: () => void
}> = ({ tokens, onTokenSelect, onAddToken, onRemoveToken, onClose }) => {
  const [search, setSearch] = useState("")
  const [error, setError] = useState("")

  const handleSearchChange = async (searchValue: string) => {
    setSearch(searchValue)
    setError("")

    if (typeof searchValue !== "string" || searchValue.length !== 42) {
      return
    }

    const isValidAddress = isAddress(searchValue, { strict: false })

    if (!isValidAddress) {
      setError("Not a valid address")
      return
    }

    const searchAddress = getAddress(searchValue)

    // Check if token already exists
    const foundToken = tokens.find(
      (token) => token.address.toViemAddress().toLowerCase() === searchAddress.toLowerCase(),
    )
    if (foundToken) {
      setError(`${foundToken.symbol} is already in your local tokens`)
      setSearch("")
      return
    }

    // Add token if handler is provided
    if (onAddToken) {
      try {
        await onAddToken(searchAddress)
        setSearch("")
        setError("")
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to add token")
      }
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Form.Root
        className=""
        onSubmit={(event) => {
          event.preventDefault()
        }}
      >
        <Search
          placeholder="Enter an address"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          variant="dialog"
        />
        <ShowWhenTrue when={error.length > 0}>
          <ShortMessageBox icon={<WarningSquareIcon size={16} />} variant="error" size="sm" className="mt-4">
            {error}
          </ShortMessageBox>
        </ShowWhenTrue>
      </Form.Root>

      <TokenList tokens={tokens} onTokenSelect={onTokenSelect} maxHeight={352} />
    </div>
  )
}
